// What the selected team is for (kanban 16a22d06).
//
// AI/coding check-in 2026-10-19: Ravi Joshi owes the Delta / Alpha / Sigma
// definitions by email (PJ copied to amend); Scott Alter: the definitions
// "centralize" in one place and are then used "for each team on what things
// they are working on". When the roadmap is narrowed to a team, this banner
// shows that team's definition and mandate areas straight from the registry
// (the same record the Quality door and Merlin's kickoff read), so the view
// says what the team is before listing what it is doing. The team's charter
// (kanban 87ab737b), where one is on record, folds behind "Read the charter".

import type { TeamMandate } from '../../services/roadmapReports';
import type { TeamColors } from './roadmapViewUtils';
import { briefsFor, foldTeamName } from './teamBriefUtils';
import styles from './RoadmapV2.module.css';

interface Props {
  /** The teams the page is narrowed to (filters.teams), in the order chosen. */
  teams: readonly string[];
  mandates: readonly TeamMandate[];
  teamColors?: TeamColors;
}

export function TeamBriefBanner({ teams, mandates, teamColors }: Props) {
  if (teams.length === 0) return null;
  const briefs = briefsFor(teams, mandates);
  return (
    <div className={styles.teamBriefs} data-testid="team-briefs">
      {briefs.map(({ team, mandate }) => {
        const colour = teamColors?.[team] ?? teamColors?.[foldTeamName(team)];
        return (
          <section
            key={team}
            className={styles.teamBrief}
            aria-label={`What ${team} is for`}
            data-testid={`team-brief-${team}`}
            style={colour ? { borderLeftColor: colour } : undefined}
          >
            <div className={styles.teamBriefHead}>
              <h3>{team}</h3>
              {mandate && (
                <span className={styles.meta}>
                  {mandate.basis ? `${mandate.basis} · ` : ''}set by {mandate.set_by_email} on {mandate.set_at.slice(0, 10)}
                </span>
              )}
            </div>
            {mandate?.definition ? (
              <p className={styles.teamBriefDefinition}>{mandate.definition}</p>
            ) : (
              <p className={styles.muted}>
                {mandate ? 'No definition written yet — an admin adds it under Org chart → What each task force is for.' : 'Not a registered task force — no definition on record.'}
              </p>
            )}
            {mandate?.charter && (
              <details className={styles.teamBriefCharter} data-testid={`team-brief-charter-${team}`}>
                <summary>Read the charter</summary>
                <pre className={styles.mandateCharterText}>{mandate.charter}</pre>
              </details>
            )}
            {mandate && mandate.areas.length > 0 && (
              <ul className={styles.teamBriefAreas} aria-label={`Mandate areas of ${team}`}>
                {mandate.areas.map((a) => (
                  <li key={a.key} className={styles.teamBriefArea} title={a.description || undefined}>
                    {a.title}
                  </li>
                ))}
              </ul>
            )}
          </section>
        );
      })}
    </div>
  );
}
